import { ConfirmationDialog } from "@/components/dialogs";
import { ErrorAlert } from "../components/alerts";
import { getFullPath, routes } from "../routes";
import { Project } from "@/api/project";
import { ProjectSchema } from "./ProjectSchema";
import { SavePrompt } from "@/components";
import { SimilarProjects } from "./components/SimilarProjects";
import { SpinButton } from "@/components/form/SpinButton";
import { useForm } from "react-hook-form";
import { useLoadingScreen, useModalDialog } from "@/hooks";
import { useNavigate } from "react-router";
import { useProjectValidation, useSaveProject, useSimilarProjects } from "./hooks";
import { zodResolver } from "@hookform/resolvers/zod";

export function ProjectForm({ data }: { data: Project }) {
  const save = useSaveProject();
  const dialog = useModalDialog();
  const navigate = useNavigate();
  const loader = useLoadingScreen();

  const form = useForm<Project>({
    defaultValues: data,
    resolver: zodResolver(ProjectSchema),
    reValidateMode: "onChange",
  });

  const formData = form.watch();
  const similar = useSimilarProjects(formData);
  const validate = useProjectValidation(form);
  const errors = form.formState.errors;
  const isDirty = Object.keys(form.formState.dirtyFields).length > 0;

  function onSuccess(project: Project) {
    navigate(getFullPath(routes.project.open(project.id)), {
      state: { skipSavePrompt: true },
    });
  }

  async function handleSaveProject() {
    loader.show();
    if ((await validate(formData)) !== true) {
      loader.hide();
    } else if (similar.data && similar.data.length > 0) {
      saveWithConfirmation();
    } else {
      save.mutate(formData, { onSuccess });
    }
  }

  function saveWithConfirmation() {
    loader.hide(true);
    dialog.show(ConfirmationDialog, {
      caption: "Similar Projects",
      message: "There are similar projects. Do you want to continue saving?",
      onAccept: () => {
        loader.show();
        save.mutate(formData, { onSuccess });
      },
    });
  }

  return (
    <SavePrompt when={isDirty}>
      <div style={{ maxWidth: 500 }}>
        <h3>Project</h3>
        <form className="mb-3" onSubmit={form.handleSubmit(handleSaveProject)} autoComplete="off">
          {/* Project Name */}
          <div>
            <label htmlFor="name" className="col-form-label pb-0">
              Project Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${errors.name && "border-danger"}`}
              maxLength={100}
              {...form.register("name")}
            />
            {errors.name && <span className="text-danger">{errors.name.message}</span>}
          </div>
          {/* Project Number */}
          <div>
            <label htmlFor="number" className="col-form-label pb-0">
              Project Number <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${errors.number && "border-danger"}`}
              maxLength={50}
              {...form.register("number")}
            />
            {errors.number && <span className="text-danger">{errors.number.message}</span>}
          </div>
          {/* Description */}
          <div>
            <label htmlFor="description" className="col-form-label pb-0">
              Description <span className="text-danger">*</span>
            </label>
            <textarea
              className={`form-control ${errors.description && "border-danger"}`}
              rows={7}
              maxLength={100}
              {...form.register("description")}
            />
            {errors.description && <span className="text-danger">{errors.description.message}</span>}
          </div>

          <SpinButton type="submit" className="btn btn-default mt-2" disabled={loader.visible || isDirty !== true}>
            Save
          </SpinButton>
        </form>

        {errors.root && <ErrorAlert>{errors.root?.message}</ErrorAlert>}

        {similar.data && similar.data.length > 0 && isDirty && (
          <div className="mt-1">
            <h4>Similar Projects</h4>
            <SimilarProjects project={formData} similar={similar.data} />
          </div>
        )}
      </div>
    </SavePrompt>
  );
}
